/**
 * AI Chatbot Mixpanel event constants.
 * This file mirrors iOS app events (with first letter capitalized) and backend events (if applicable).
 */

/**
 * Maps to iOS AIChatbotAssistantType enum.
 */
export const AIChatbotAssistantType = Object.freeze({
    GENERAL: 'general_assistant',
    VIDEO: 'video_assistant',
});

/**
 * Maps to iOS AIChatbotQuestionSource enum.
 */
export const AIChatbotQuestionSource = Object.freeze({
    PREDEFINED_QUESTION: 'predefined_question',
    CUSTOM_QUESTION: 'custom_question',
    NEXT_BEST_QUESTION: 'next_best_question',
    DYNAMIC_QUESTION: 'dynamic_question',
});

/**
 * Trigger type for expand/collapse events.
 * Distinguishes between automatic (system-initiated) and manual (user-initiated) actions.
 */
export const AIChatbotCollapseTrigger = Object.freeze({
    AUTO: 'auto',
    MANUAL: 'manual',
});

/**
 * Event names for AI Chatbot Mixpanel events.
 * Names match iOS app events (with first letter capitalized).
 */
export const AIChatbotEvents = Object.freeze({
    LOADED: 'AiChatbotLoaded',
    OPENED: 'AiChatbotOpened',
    EXPANDED: 'AiChatbotExpanded',
    COLLAPSED: 'AiChatbotCollapsed',
    URL_SELECTED: 'AiChatbotURLSelected',
    VIDEO_OPENED: 'AiChatbotVideoOpened',
    VIDEO_SEEKED: 'AiChatbotVideoSeeked',
    QUESTION_SUBMITTED: 'AiChatbotQuestionSubmitted',
    RESPONSE_STREAM_STARTED: 'AiChatbotResponseStreamStarted',
    RESPONSE_STREAM_FINISHED: 'AiChatbotResponseStreamFinished',
    RESPONSE_STREAM_FAILED: 'AiChatbotResponseStreamFailed',

    // Tooltips
    INFO_MODAL_APPEAR: 'AiChatbotVideoInfoModalViewAppear',
    INFO_MODAL_CLICKED: 'AiChatbotVideoInfoModalButtonSelected',
    TOOLTIP_APPEAR: 'AiChatbotVideoTooltipViewAppear',
    TOOLTIP_CLICKED: 'AiChatbotVideoTooltipButtonSelected',

    // In-Video Prompts
    IN_VIDEO_PROMPT_SHOWN: 'AiChatbotInVideoPromptShown',
    IN_VIDEO_PROMPT_CLICKED: 'AiChatbotInVideoPromptClicked',
    IN_VIDEO_PROMPT_DISMISSED: 'AiChatbotInVideoPromptDismissed',
    IN_VIDEO_PROMPT_TIMEOUT: 'AiChatbotInVideoPromptTimeout',
    IN_VIDEO_PROMPT_BLOCKED: 'AiChatbotInVideoPromptBlocked',
});

/**
 * Get assistant type based on whether we're on a video page.
 * @param {string|null} videoSlug - The video slug if on a video page, null otherwise.
 * @returns {string} The assistant type value.
 */
export const getAssistantType = (videoSlug) => {
    return videoSlug ? AIChatbotAssistantType.VIDEO : AIChatbotAssistantType.GENERAL;
};

/**
 * Track an AI Chatbot event via Mixpanel.
 * @param {string} eventName - The event name from AIChatbotEvents.
 * @param {Object} properties - Event properties.
 */
export const trackAIChatbotEvent = (eventName, properties = {}) => {
    window.vumedi?.mixpanel?.track(eventName, properties);
};
